import { useEffect, useLayoutEffect, useRef } from "react";
import type { BatchUpload } from "./upload/batchUpload.types";
import { useBatchUpload } from "./upload/useBatchUpload";
import { useLeaveGuard } from "./upload/useLeaveGuard";
import { batchSettledKey } from "./batchUploadHost";

/**
 * Dávka hostitele panelu nahrávání (stránka Média, okno výběru fotek; vize
 * podnety-tymu-2026-10, F7.8): model dávky, hlídání odchodu nad jejím během a detektor konce
 * dávky. `onSettled` se zavolá, kdykoli se nenullový otisk `batchSettledKey` změní (konec
 * dávky, konec opakování, obnova z koše po konci); čte se přes ref, takže nestabilní funkce
 * hostitele efekt nespouští znovu. Odpojení hostitele dávku vynuluje (`useBatchUpload`);
 * odpojení během běhu zavolá `onSettled` naposledy (přerušená dávka).
 */
export function useHostedBatch(onSettled: () => void): {
  batch: BatchUpload;
  guard: ReturnType<typeof useLeaveGuard>;
} {
  const batch = useBatchUpload();
  const guard = useLeaveGuard(batch.running);

  const onSettledRef = useRef(onSettled);
  const runningRef = useRef(batch.running);
  useLayoutEffect(() => {
    onSettledRef.current = onSettled;
    runningRef.current = batch.running;
  });

  const settledKey = batchSettledKey(batch);
  const lastSettled = useRef<string | null>(null);
  useEffect(() => {
    if (settledKey === null || settledKey === lastSettled.current) return;
    lastSettled.current = settledKey;
    onSettledRef.current();
  }, [settledKey]);

  // Odpojení během běhu (zavřené okno, „Odejít“ z odkazu nebo tlačítka Zpět): otisk konce už
  // nevznikne, ale letící požadavky na serveru doběhnou a fotky vzniknou. Seznamy se proto
  // obnoví tady, ať je hostitel po návratu (`staleTime` 5 min) ukáže.
  useEffect(
    () => () => {
      if (runningRef.current) onSettledRef.current();
    },
    [],
  );

  return { batch, guard };
}
